import chroma from "chroma-js";
import {Lookup, UtilsService} from "framework/services/utils.service";
import {ControlOption} from "framework/ui/control-option";

function safeChroma(from: string | number): chroma.Color
{
    try
    {
        return chroma(from);
    }
    catch (e)
    {
        return chroma("black");
    }
}

export class Colorized<T>
{
    constructor(public readonly model: T)
    {
    }

    color: string;
}

export class ColorOption
{
    private m_chroma: chroma.Color;

    set color(color: string)
    {
        this.m_chroma = safeChroma(color);
    }

    get hex(): string
    {
        return this.m_chroma.hex("rgb");
    }

    get color(): string
    {
        return this.m_chroma.css();
    }

    get hsl(): number[]
    {
        return this.m_chroma.hsl();
    }

    get rgb(): number[]
    {
        return this.m_chroma.rgb();
    }

    constructor(public id: string,
                color: string,
                public name: string)
    {
        this.color = color;
    }
}

//--//

export class ColorScheme
{
    public readonly colors: string[];

    public static readonly defaultColors = [
        "#5eed5e",
        "#ed5e76"
    ];

    constructor(colors?: string[])
    {
        if (!colors || colors.length === 0)
        {
            colors = ColorScheme.defaultColors;
        }
        else if (colors.length === 1)
        {
            colors.unshift("#FFFFFF");
        }

        this.colors = colors;
    }
}

/*
 * We define this interface to mirror the ColorGradient data model
 * since we cannot import server models into framework components.
 */
export interface IColorGradient
{
    computeStops(dataMin: number,
                 dataMax: number): ColorGradientStop[];

    computeRangeMin(dataMin: number): number;

    computeRangeMax(dataMax: number): number;

    isValid(): boolean;
}

export class ColorGradientStop
{
    /**
     * @param value - The value at which the gradient should reflect the specified color
     * @param color - Hex color value
     */
    constructor(public value: number,
                public color: string)
    {
    }

    clone(): ColorGradientStop
    {
        return new ColorGradientStop(this.value, this.color);
    }
}

export class ColorGradientExtended
{
    constructor(public model: IColorGradient)
    {
        if (!this.model)
        {
            this.model = {
                computeStops(dataMin: number,
                             dataMax: number): ColorGradientStop[]
                {
                    return [
                        new ColorGradientStop(dataMin, ColorGradientExtended.getDefaultStartColor()),
                        new ColorGradientStop(dataMax, ColorGradientExtended.getDefaultEndColor())
                    ];
                },

                computeRangeMin(dataMin: number): number { return dataMin; },

                computeRangeMax(dataMax: number): number { return dataMax; },

                isValid(): boolean { return true; }
            };
        }
    }

    static getDefaultStartColor(): string
    {
        return ChartColorUtilities.getDefaultColorById("green").hex;
    }

    static getDefaultEndColor(): string
    {
        return ChartColorUtilities.getDefaultColorById("red").hex;
    }
}

export abstract class ColorMapper
{
    get colorStops(): ColorGradientStop[]
    {
        return this.m_colorStops.map((colorStop) => colorStop.clone());
    }

    protected readonly m_colorStops: ColorGradientStop[];

    constructor(public readonly interpolationMode: ColorSegmentInterpolationMode,
                colorStops: ColorGradientStop[])
    {
        this.m_colorStops = ColorMapper.cleanGradientStops(colorStops);
    }

    abstract getColor(value: number): string;

    static contextualizeGradientStops(stops: ColorGradientStop[],
                                      min: number,
                                      max: number): ColorGradientStop[]
    {
        stops = stops.map((stop) => stop.clone());

        // fill in min/max from Models.ColorConfiguration.segments
        if (stops[0].value == null) stops[0].value = min;
        if (stops[stops.length - 1].value == null) stops[stops.length - 1].value = max;

        // fill in midpoints
        for (let i = 1; i < stops.length - 1; i++)
        {
            let stop = stops[i];
            if (stop.value == null) stop.value = (stops[i - 1].value + stops[i + 1].value) / 2;
        }

        return stops;
    }

    static cleanGradientStops(stops: ColorGradientStop[]): ColorGradientStop[]
    {
        stops = stops.map((stop) => stop.clone());

        let customMin;
        let customMax;
        for (let i = 1; i < stops.length - 1; i++)
        {
            let stop = stops[i];
            if (!isNaN(stop.value))
            {
                customMin = Math.min(stop.value, customMin ?? Number.MAX_VALUE);
                customMax = Math.max(stop.value, customMax ?? -Number.MAX_VALUE);
            }
        }

        if (customMin != undefined && stops[0].value > customMin)
        {
            let deleteCount = stops[1].value > customMin ? 2 : 1;
            stops.splice(0, deleteCount);
        }

        if (customMax != undefined && stops[stops.length - 1].value < customMax)
        {
            let deleteCount = stops.length > 3 && stops[stops.length - 2].value < customMax ? 2 : 1;
            stops.splice(stops.length - deleteCount, deleteCount);
        }

        for (let i = 1; i < stops.length - 1; i++)
        {
            let prevValue = stops[i - 1].value;
            let nextValue = stops[i + 1].value;
            if (!isNaN(prevValue) && !isNaN(nextValue) && prevValue === nextValue)
            {
                stops.splice(i--, 1);
            }
        }

        return stops;
    }
}

export class StepwiseColorMapper extends ColorMapper
{
    constructor(colorStops: ColorGradientStop[])
    {
        super(ColorSegmentInterpolationMode.STEP, colorStops);
    }

    getColor(value: number): string
    {
        // go from left to right to respect custom values that are not necessarily contained within min/max range
        let afterIdx = this.m_colorStops.findIndex((stop) => stop.value > value);

        let stop: ColorGradientStop;
        switch (afterIdx)
        {
            case -1:
                stop = this.m_colorStops[this.m_colorStops.length - 1];
                break;

            case 0:
                stop = this.m_colorStops[0];
                break;

            default:
                stop = this.m_colorStops[afterIdx - 1];
                break;
        }

        return stop?.color;
    }

    static fromEnumsToColorMapper(enumValues: { value: number }[],
                                  paletteId: PaletteId): StepwiseColorMapper
    {
        let colors: string[] = [];
        let stops            = enumValues.map((enumValue) =>
                                              {
                                                  let color = ChartColorUtilities.nextBestColor(colors, paletteId);
                                                  return new ColorGradientStop(enumValue.value, color);
                                              });
        let lastStop         = stops[stops.length - 1];
        stops.push(new ColorGradientStop(lastStop.value, lastStop.color));

        return new StepwiseColorMapper(stops);
    }
}

export class ColorGradientContinuous extends ColorMapper
{
    private static readonly minStepsPerDaisyLink: number               = 5;
    protected static readonly lowPrecisionDaisyChainLength: number     = 32;
    private static readonly maxColorGradientLength: number             = 230;
    private static readonly defaultColorMode: chroma.InterpolationMode = "lch";

    protected readonly scales: chroma.Scale[];

    get min(): number
    {
        return this.m_colorStops[0].value;
    }

    get max(): number
    {
        return this.m_colorStops[this.m_colorStops.length - 1].value;
    }

    constructor(colorStops: ColorGradientStop[],
                private readonly colorMode: chroma.InterpolationMode = ColorGradientContinuous.defaultColorMode)
    {
        super(ColorSegmentInterpolationMode.LINEAR, colorStops);

        this.scales  = [];
        let nextStop = this.m_colorStops[0];
        for (let i = 1; i < this.m_colorStops.length; i++)
        {
            let prevStop = nextStop;
            nextStop     = this.m_colorStops[i];

            this.scales.push(chroma.scale([
                                              prevStop.color,
                                              nextStop.color
                                          ])
                                   .domain([
                                               prevStop.value,
                                               nextStop.value
                                           ])
                                   .mode(this.colorMode)
                                   .correctLightness());
        }
    }

    protected getBucketIndex(value: number): number
    {
        value = UtilsService.clamp(this.min, this.max, value);

        let index = 0;
        for (let stop of this.m_colorStops) index += Math.ceil(UtilsService.clamp(0, 1, value - stop.value));

        return Math.max(0, index - 1);
    }

    getColor(value: number): string
    {
        return this.scales[this.getBucketIndex(value)](value)
            .hex();
    }

    getCanvasGradient(canvas: CanvasRenderingContext2D,
                      x1: number,
                      y1: number,
                      x2: number,
                      y2: number): CanvasGradient
    {
        let gradient = canvas.createLinearGradient(x1, y1, x2, y2);
        let colors   = ColorGradientContinuous.generateGradientColors(this.m_colorStops, false);
        if (colors.length === 1) colors.push(colors[0]);
        let divider = colors.length - 1;
        for (let i = 0; i < colors.length; i++) gradient.addColorStop(i / divider, colors[i]);

        return gradient;
    }

    // returns colors for a correctly weighted gradient or multi-gradient
    static generateGradientColors(colorStops: ColorGradientStop[],
                                  highPrecision: boolean              = true,
                                  numColorsOverride?: number,
                                  colorMode: chroma.InterpolationMode = ColorGradientContinuous.defaultColorMode): string[]
    {
        let numStops = colorStops.length;

        if (numStops > 2) // certain permutations of scale colors generate messed up multi-step gradients: create them separately and then combine
        {
            let totalRange = colorStops[numStops - 1].value - colorStops[0].value;
            let minRange   = totalRange;
            for (let i = 1; i < numStops; i++)
            {
                let diff = colorStops[i].value - colorStops[i - 1].value;
                if (diff > 0) minRange = Math.min(diff, minRange);
            }

            let linksPerUnitRange = ColorGradientContinuous.minStepsPerDaisyLink / minRange;
            if (highPrecision) linksPerUnitRange *= 5;
            let numColors = totalRange * linksPerUnitRange;

            let multiplier = 1;
            if (!isNaN(numColorsOverride)) multiplier = numColorsOverride / numColors;

            if (!highPrecision && numColors > ColorGradientContinuous.lowPrecisionDaisyChainLength)
            {
                multiplier = ColorGradientContinuous.lowPrecisionDaisyChainLength / numColors;
            }
            else if (highPrecision && numColors > ColorGradientContinuous.maxColorGradientLength)
            {
                multiplier = ColorGradientContinuous.maxColorGradientLength / numColors;
            }
            multiplier *= linksPerUnitRange;

            let gradients: string[][] = [];
            let next                  = colorStops[0];
            for (let i = 1; i < colorStops.length; i++)
            {
                let prev = next;
                next     = colorStops[i];

                if (prev.value == next.value) continue;

                let scale = chroma.scale([
                                             prev.color,
                                             next.color
                                         ])
                                  .mode(colorMode)
                                  .correctLightness();

                let valueRange = 1;
                if (!isNaN(next.value) && !isNaN(prev.value))
                {
                    scale      = scale.domain([
                                                  prev.value,
                                                  next.value
                                              ]);
                    valueRange = next.value - prev.value;
                }

                let colors = scale.colors(Math.max(1, valueRange * multiplier));
                gradients.push(colors);
            }

            let daisyChainedGradients = [gradients[0][0]];
            for (let gradient of gradients)
            {
                for (let i = 1; i < gradient.length; i++)
                {
                    daisyChainedGradients.push(gradient[i]);
                }
            }

            return daisyChainedGradients;
        }
        else if (numStops === 2)
        {
            let numColors = numColorsOverride || (highPrecision ? ColorGradientContinuous.lowPrecisionDaisyChainLength : Math.round(ColorGradientContinuous.lowPrecisionDaisyChainLength / 2));
            return chroma.scale(colorStops.map((stop) => stop.color))
                         .mode(colorMode)
                         .correctLightness()
                         .colors(numColors);
        }
        else
        {
            return colorStops.map((colorStop) => colorStop.color);
        }
    }
}

export class ColorGradientDiscrete extends ColorGradientContinuous
{
    private readonly colorBuckets: ColorGradientBucket[];

    constructor(colorStops: ColorGradientStop[],
                numColors: number = ColorGradientContinuous.lowPrecisionDaisyChainLength,
                colorMode?: chroma.InterpolationMode)
    {
        super(colorStops, colorMode);

        this.colorBuckets = [];
        let currValue     = this.m_colorStops[0].value;
        for (let i = 0; i < this.scales.length; i++)
        {
            let nextValue           = this.m_colorStops[i + 1].value;
            let precomputedGradient = new ColorGradientBucket(currValue, nextValue, this.scales[i], numColors);
            this.colorBuckets.push(precomputedGradient);
            currValue = nextValue;
        }
    }

    getColor(value: number): string
    {
        return this.colorBuckets[this.getBucketIndex(value)].getColor(value);
    }
}

class ColorGradientBucket
{
    private readonly diff: number;
    private colors: string[];

    constructor(private readonly min: number,
                max: number,
                scale: chroma.Scale,
                numColors: number)
    {
        this.diff = max - min;
        if (this.diff)
        {
            this.colors = scale.colors(numColors);
        }
        else
        {
            let onlyColor = scale(max)
                .hex();
            this.colors   = [onlyColor];
        }
    }

    getColor(value: number)
    {
        let decimal = (value - this.min) / this.diff;
        let index   = UtilsService.clamp(0, this.colors.length - 1, Math.floor(decimal * this.colors.length) || 0);
        return this.colors[index];
    }
}

export enum ColorSegmentInterpolationMode
{
    STEP   = "STEP",
    LINEAR = "LINEAR"
}

export class ColorSegmentBackground
{
    public readonly background: string;

    constructor(startColor: string,
                endColor: string,
                mode: ColorSegmentInterpolationMode)
    {
        if (mode === ColorSegmentInterpolationMode.LINEAR && startColor && endColor)
        {
            this.background = `linear-gradient(90deg, ${ColorGradientContinuous.generateGradientColors([
                                                                                                           new ColorGradientStop(0, startColor),
                                                                                                           new ColorGradientStop(1, endColor)
                                                                                                       ], false)} 100%)`;
        }
        else
        {
            this.background = startColor || "unset";
        }
    }
}

function getGradientPalette(name: string,
                            color1: string,
                            color2: string,
                            paletteSize: number    = 10,
                            darkenAmount: number   = 0.1,
                            brightenAmount: number = 0.25): ColorOption[]
{
    let dark   = true;
    let colors = ColorGradientContinuous.generateGradientColors([
                                                                    new ColorGradientStop(0, color1),
                                                                    new ColorGradientStop(1, color2)
                                                                ], undefined, paletteSize)
                                        .map((color,
                                              idx) =>
                                             {
                                                 if (idx === 0 || idx === paletteSize - 1) return color;
                                                 let chromaColor = safeChroma(color);
                                                 chromaColor     = dark ? chromaColor.darken(darkenAmount) : chromaColor.brighten(brightenAmount);
                                                 dark            = !dark;
                                                 return chromaColor.hex();
                                             });

    return colors.map((color,
                       idx) => new ColorOption(name.toLocaleLowerCase() + idx, color, color));
}

function mapPalette(colors: ColorOption[]): Lookup<ColorOption>
{
    return UtilsService.extractLookup(colors);
}

export const BackgroundColorPalette = {
    ghostWhite  : new ColorOption("ghostWhite", "#F5F6FB", "Ghost White"),
    whiteSmoke  : new ColorOption("whiteSmoke", "#F5F5F5", "White Smoke"),
    naturalWhite: new ColorOption("naturalWhite", "#FFFFFF", "Natural White"),
    lightGrey   : new ColorOption("lightGrey", "#F9F9F9", "Light Grey"),
    inputBG     : new ColorOption("InputBG", "#FCFDFF", "Input BG")
} as const;

export const DefaultColorPalette = {
    aqua    : new ColorOption("aqua", "hsl(195,80%,65%)", "Aqua"),
    blue    : new ColorOption("blue", "hsl(215,80%,65%)", "Blue"),
    lavender: new ColorOption("lavender", "hsl(240,80%,65%)", "Lavender"),
    purple  : new ColorOption("purple", "hsl(285,80%,65%)", "Purple"),
    pink    : new ColorOption("pink", "hsl(325,80%,65%)", "Pink"),
    red     : new ColorOption("red", "hsl(350,80%,65%)", "Red"),
    orange  : new ColorOption("orange", "hsl(15,80%,65%)", "Orange"),
    yellow  : new ColorOption("yellow", "hsl(50,80%,65%)", "Yellow"),
    green   : new ColorOption("green", "hsl(120,80%,65%)", "Green"),
    teal    : new ColorOption("teal", "hsl(160,80%,65%)", "Teal")
} as const;

export const CoreStackChartColorPalette = {
    coreStackBlue         : new ColorOption("coreStackBlue", "#0076E1", "CoreStack Blue"),
    coreStackRed          : new ColorOption("coreStackRed", "#EA5F82", "CoreStack Red"),
    coreStackOrange       : new ColorOption("coreStackOrange", "#FFA384", "CoreStack Orange"),
    coreStackGreen        : new ColorOption("coreStackGreen", "#00AAA5", "CoreStack Green"),
    coreStackDarkGreen    : new ColorOption("coreStackDarkGreen", "#5fa86b", "CoreStack Dark Green"),
    coreStackBrown        : new ColorOption("coreStackBrown", "#8C7444", "CoreStack Brown"),
    coreStackPink         : new ColorOption("coreStackPink", "#DB65AD", "CoreStack Pink"),
    coreStackLavender     : new ColorOption("coreStackLavender", "#657FCE", "CoreStack Lavender"),
    coreStackYellow       : new ColorOption("coreStackYellow", "#FFCA5D", "CoreStack Yellow"),
    coreStackPurple       : new ColorOption("coreStackPurple", "#6459DE", "CoreStack Purple"),
    coreStackAquamarine   : new ColorOption("coreStackAquamarine", "#6DCFE3", "CoreStack Aquamarine"),
    coreStackYellowGreen  : new ColorOption("coreStackYellowGreen", "#8AC926", "CoreStack YellowGreen"),
    coreStackPelorous     : new ColorOption("coreStackPelorous", "#1982C4", "CoreStack Pelorous"),
    coreStackBayOfMany    : new ColorOption("coreStackBayOfMany", "#3D405B", "CoreStack BayOfMany"),
    coreStackPadua        : new ColorOption("coreStackPadua", "#81B29A", "CoreStack Padua"),
    coreStackBurntSienna  : new ColorOption("coreStackBurntSienna", "#EE6C4D", "CoreStack BurntSienna"),
    coreStackRoseBudCherry: new ColorOption("coreStackRoseBudCherry", "#8F2D56", "CoreStack RoseBudCherry"),
    coreStackMediumPurple : new ColorOption("coreStackMediumPurple", "#985DE5", "CoreStack MediumPurple"),
    coreStackMalta        : new ColorOption("coreStackMalta", "#A99985", "CoreStack Malta"),
    coreStackBondiBlue    : new ColorOption("coreStackBondiBlue", "#00A0B5", "CoreStack BondiBlue"),
    coreStackOrinoco      : new ColorOption("coreStackOrinoco", "#CBD5A9", "CoreStack Orinoco"),
    coreStackGrey         : new ColorOption("coreStackGrey", "#808080", "CoreStack Grey")
} as const;

export const CoreStackBlueGradient = {
    blue100: new ColorOption("blue100", "#0076E1", "Blue 100"),
    blue90 : new ColorOption("blue90", "#1B85E4", "Blue 90"),
    blue80 : new ColorOption("blue80", "#3693E8", "Blue 80"),
    blue70 : new ColorOption("blue70", "#51A2EB", "Blue 70"),
    blue60 : new ColorOption("blue60", "#6CB0EE", "Blue 60"),
    blue50 : new ColorOption("blue50", "#86BFF2", "Blue 50"),
    blue40 : new ColorOption("blue40", "#A1CDF5", "Blue 40"),
    blue30 : new ColorOption("blue30", "#BCDCF8", "Blue 30"),
    blue20 : new ColorOption("blue20", "#D7EAFC", "Blue 20"),
    blue10 : new ColorOption("blue10", "#F2F9FF", "Blue 10")
} as const;

export const CoreStackRedGradient = {
    red100: new ColorOption("red100", "#EA5F82", "Red 100"),
    red90 : new ColorOption("red90", "#EC6F8E", "Red 90"),
    red80 : new ColorOption("red80", "#EF7E9B", "Red 80"),
    red70 : new ColorOption("red70", "#F18EA7", "Red 70"),
    red60 : new ColorOption("red60", "#F39EB3", "Red 60"),
    red50 : new ColorOption("red50", "#F6ADC0", "Red 50"),
    red40 : new ColorOption("red40", "#F8BDCC", "Red 40"),
    red30 : new ColorOption("red30", "#FACDD8", "Red 30"),
    red20 : new ColorOption("red20", "#FDDCE5", "Red 20"),
    red10 : new ColorOption("red10", "#FFECF1", "Red 10")
} as const;

export const CoreStackOrangeGradient = {
    orange100: new ColorOption("orange100", "#FFA384", "Orange 100"),
    orange90 : new ColorOption("orange90", "#FFAC90", "Orange 90"),
    orange80 : new ColorOption("orange80", "#FFB59C", "Orange 80"),
    orange70 : new ColorOption("orange70", "#FFBEA8", "Orange 70"),
    orange60 : new ColorOption("orange60", "#FFC7B4", "Orange 60"),
    orange50 : new ColorOption("orange50", "#FFD1C1", "Orange 50"),
    orange40 : new ColorOption("orange40", "#FFDACD", "Orange 40"),
    orange30 : new ColorOption("orange30", "#FFE3D9", "Orange 30"),
    orange20 : new ColorOption("orange20", "#FFECE5", "Orange 20"),
    orange10 : new ColorOption("orange10", "#FFF5F1", "Orange 10")
} as const;

export const CoreStackGreenGradient = {
    green100: new ColorOption("green100", "#00AAA5", "Green 100"),
    green90 : new ColorOption("green90", "#1BB3AF", "Green 90"),
    green80 : new ColorOption("green80", "#36BDB9", "Green 80"),
    green70 : new ColorOption("green70", "#51C6C3", "Green 70"),
    green60 : new ColorOption("green60", "#6CD0CD", "Green 60"),
    green50 : new ColorOption("green50", "#86D9D7", "Green 50"),
    green40 : new ColorOption("green40", "#A1E3E1", "Green 40"),
    green30 : new ColorOption("green30", "#BCECEB", "Green 30"),
    green20 : new ColorOption("green20", "#D7F6F5", "Green 20"),
    green10 : new ColorOption("green10", "#F2FFFF", "Green 10")
} as const;

export const CoreStackBrownGradient = {
    brown100: new ColorOption("brown100", "#8C7444", "Brown 100"),
    brown90 : new ColorOption("brown90", "#998357", "Brown 90"),
    brown80 : new ColorOption("brown80", "#A6926A", "Brown 80"),
    brown70 : new ColorOption("brown70", "#B2A17D", "Brown 70"),
    brown60 : new ColorOption("brown60", "#BFB090", "Brown 60"),
    brown50 : new ColorOption("brown50", "#CCBEA4", "Brown 50"),
    brown40 : new ColorOption("brown40", "#D9CDB7", "Brown 40"),
    brown30 : new ColorOption("brown30", "#E5DCCA", "Brown 30"),
    brown20 : new ColorOption("brown20", "#F2EBDD", "Brown 20"),
    brown10 : new ColorOption("brown10", "#FFFAF0", "Brown 10")
} as const;

export const CoreStackPinkGradient = {
    pink100: new ColorOption("pink100", "#DB65AD", "Pink 100"),
    pink90 : new ColorOption("pink90", "#DF75B6", "Pink 90"),
    pink80 : new ColorOption("pink80", "#E385BE", "Pink 80"),
    pink70 : new ColorOption("pink70", "#E794C7", "Pink 70"),
    pink60 : new ColorOption("pink60", "#EBA4D0", "Pink 60"),
    pink50 : new ColorOption("pink50", "#EFB4D8", "Pink 50"),
    pink40 : new ColorOption("pink40", "#F3C4E1", "Pink 40"),
    pink30 : new ColorOption("pink30", "#F7D3EA", "Pink 30"),
    pink20 : new ColorOption("pink20", "#FBE3F2", "Pink 20"),
    pink10 : new ColorOption("pink10", "#FFF3FB", "Pink 10")
} as const;

export const CoreStackLavenderGradient = {
    lavender100: new ColorOption("lavender100", "#657FCE", "Lavender 100"),
    lavender90 : new ColorOption("lavender90", "#758CD3", "Lavender 90"),
    lavender80 : new ColorOption("lavender80", "#859AD9", "Lavender 80"),
    lavender70 : new ColorOption("lavender70", "#95A7DE", "Lavender 70"),
    lavender60 : new ColorOption("lavender60", "#A5B5E4", "Lavender 60"),
    lavender50 : new ColorOption("lavender50", "#B6C2E9", "Lavender 50"),
    lavender40 : new ColorOption("lavender40", "#C6D0EF", "Lavender 40"),
    lavender30 : new ColorOption("lavender30", "#D6DDF4", "Lavender 30"),
    lavender20 : new ColorOption("lavender20", "#E6EBFA", "Lavender 20"),
    lavender10 : new ColorOption("lavender10", "#F6F8FF", "Lavender 10")
} as const;

export const CoreStackYellowGradient = {
    yellow100: new ColorOption("yellow100", "#FFCA5D", "Yellow 100"),
    yellow90 : new ColorOption("yellow90", "#FFCF6D", "Yellow 90"),
    yellow80 : new ColorOption("yellow80", "#FFD57D", "Yellow 80"),
    yellow70 : new ColorOption("yellow70", "#FFDA8E", "Yellow 70"),
    yellow60 : new ColorOption("yellow60", "#FFDF9E", "Yellow 60"),
    yellow50 : new ColorOption("yellow50", "#FFE5AE", "Yellow 50"),
    yellow40 : new ColorOption("yellow40", "#FFEABE", "Yellow 40"),
    yellow30 : new ColorOption("yellow30", "#FFEFCF", "Yellow 30"),
    yellow20 : new ColorOption("yellow20", "#FFF5DF", "Yellow 20"),
    yellow10 : new ColorOption("yellow10", "#FFFAEF", "Yellow 10")
} as const;

export const CoreStackPurpleGradient = {
    purple100: new ColorOption("purple100", "#6459DE", "Purple 100"),
    purple90 : new ColorOption("purple90", "#756BE2", "Purple 90"),
    purple80 : new ColorOption("purple80", "#857DE5", "Purple 80"),
    purple70 : new ColorOption("purple70", "#968EE9", "Purple 70"),
    purple60 : new ColorOption("purple60", "#A6A0ED", "Purple 60"),
    purple50 : new ColorOption("purple50", "#B7B2F0", "Purple 50"),
    purple40 : new ColorOption("purple40", "#C7C4F4", "Purple 40"),
    purple30 : new ColorOption("purple30", "#D8D5F8", "Purple 30"),
    purple20 : new ColorOption("purple20", "#E8E7FB", "Purple 20"),
    purple10 : new ColorOption("purple10", "#F9F9FF", "Purple 10")
} as const;

export const CoreStackAquamarineGradient = {
    aquamarine100: new ColorOption("aquamarine100", "#6DCFE3", "Aqumarine 100"),
    aquamarine90 : new ColorOption("aquamarine90", "#7CD4E6", "Aqumarine 90"),
    aquamarine80 : new ColorOption("aquamarine80", "#8CD9E9", "Aqumarine 80"),
    aquamarine70 : new ColorOption("aquamarine70", "#9BDFEC", "Aqumarine 70"),
    aquamarine60 : new ColorOption("aquamarine60", "#AAE4EF", "Aqumarine 60"),
    aquamarine50 : new ColorOption("aquamarine50", "#BAE9F3", "Aqumarine 50"),
    aquamarine40 : new ColorOption("aquamarine40", "#C9EEF6", "Aqumarine 40"),
    aquamarine30 : new ColorOption("aquamarine30", "#D8F4F9", "Aqumarine 30"),
    aquamarine20 : new ColorOption("aquamarine20", "#E8F9FC", "Aqumarine 20"),
    aquamarine10 : new ColorOption("aquamarine10", "#F7FEFF", "Aqumarine 10")
} as const;

export const CoreStackBlack = {
    black100 : new ColorOption("black100", "#F5F5F5", "Black 100"),
    black200 : new ColorOption("black200", "#E1E4E9", "Black 200"),
    black300 : new ColorOption("black300", "#C8CDD5", "Black 300"),
    black400 : new ColorOption("black400", "#AEB6C2", "Black 400"),
    black500 : new ColorOption("black500", "#959FAE", "Black 500"),
    black600 : new ColorOption("black600", "#7C879B", "Black 600"),
    black700 : new ColorOption("black700", "#637087", "Black 700"),
    black800 : new ColorOption("black800", "#495974", "Black 800"),
    black900 : new ColorOption("black900", "#172B4D", "Black 900"),
    black1000: new ColorOption("black1000", "#172B4D", "Black 1000")
} as const;

export const CoreStackBlue = {
    blue100 : new ColorOption("blue100", "#F5FAFE", "Blue 100"),
    blue200 : new ColorOption("blue200", "#EBF5FF", "Blue 200"),
    blue300 : new ColorOption("blue300", "#E3F1FF", "Blue 300"),
    blue400 : new ColorOption("blue400", "#C3DFFB", "Blue 400"),
    blue500 : new ColorOption("blue500", "#A2CEF6", "Blue 500"),
    blue600 : new ColorOption("blue600", "#61ABEE", "Blue 600"),
    blue700 : new ColorOption("blue700", "#4199EA", "Blue 700"),
    blue800 : new ColorOption("blue800", "#0076E1", "Blue 800"),
    blue900 : new ColorOption("blue900", "#005FB5", "Blue 900"),
    blue1000: new ColorOption("blue1000", "#004789", "Blue 1000")
} as const;

export const CoreStackYellow = {
    yellow100 : new ColorOption("yellow100", "#FFFAE4", "Yellow 100"),
    yellow200 : new ColorOption("yellow200", "#FDF2BE", "Yellow 200"),
    yellow300 : new ColorOption("yellow300", "#FCEB98", "Yellow 300"),
    yellow400 : new ColorOption("yellow400", "#FAE372", "Yellow 400"),
    yellow500 : new ColorOption("yellow500", "#F8DB4C", "Yellow 500"),
    yellow600 : new ColorOption("yellow600", "#F7D426", "Yellow 600"),
    yellow700 : new ColorOption("yellow700", "#F5CC00", "Yellow 700"),
    yellow800 : new ColorOption("yellow800", "#E6BA18", "Yellow 800"),
    yellow900 : new ColorOption("yellow900", "#D6A730", "Yellow 900"),
    yellow1000: new ColorOption("yellow1000", "#C79548", "Yellow 1000")
} as const;

export const CoreStackOrange = {
    orange100 : new ColorOption("orange100", "#FEEEE9", "Orange 100"),
    orange200 : new ColorOption("orange200", "#FDDED4", "Orange 200"),
    orange300 : new ColorOption("orange300", "#FCCDBE", "Orange 300"),
    orange400 : new ColorOption("orange400", "#F88A67", "Orange 400"),
    orange500 : new ColorOption("orange500", "#F77951", "Orange 500"),
    orange600 : new ColorOption("orange600", "#F6693C", "Orange 600"),
    orange700 : new ColorOption("orange700", "#F55826", "Orange 700"),
    orange800 : new ColorOption("orange800", "#DD4F22", "Orange 800"),
    orange900 : new ColorOption("orange900", "#C4461E", "Orange 900"),
    orange1000: new ColorOption("orange1000", "#933517", "Orange 1000")
} as const;

export const CoreStackGreen = {
    green100 : new ColorOption("green100", "#EEFFF8", "Green 100"),
    green200 : new ColorOption("green200", "#CFF2E4", "Green 200"),
    green300 : new ColorOption("green300", "#B1E6CF", "Green 300"),
    green400 : new ColorOption("green400", "#92D9BB", "Green 400"),
    green500 : new ColorOption("green500", "#73CCA7", "Green 500"),
    green600 : new ColorOption("green600", "#55C092", "Green 600"),
    green700 : new ColorOption("green700", "#36B37E", "Green 700"),
    green800 : new ColorOption("green800", "#24A06C", "Green 800"),
    green900 : new ColorOption("green900", "#128D59", "Green 900"),
    green1000: new ColorOption("green1000", "#007A47", "Green 1000")
} as const;

export const CoreStackTeal = {
    teal100 : new ColorOption("teal100", "#E5FBFF", "Teal 100"),
    teal200 : new ColorOption("teal200", "#BFF0F9", "Teal 200"),
    teal300 : new ColorOption("teal300", "#99E5F2", "Teal 300"),
    teal400 : new ColorOption("teal400", "#73DAEC", "Teal 400"),
    teal500 : new ColorOption("teal500", "#4CCEE6", "Teal 500"),
    teal600 : new ColorOption("teal600", "#26C3DF", "Teal 600"),
    teal700 : new ColorOption("teal700", "#00B8D9", "Teal 700"),
    teal800 : new ColorOption("teal800", "#009DC2", "Teal 800"),
    teal900 : new ColorOption("teal900", "#0081AC", "Teal 900"),
    teal1000: new ColorOption("teal1000", "#006695", "Teal 1000")
} as const;

export const CoreStackMagenta = {
    magenta100 : new ColorOption("magenta100", "#F8EBF1", "Magenta 100"),
    magenta200 : new ColorOption("magenta200", "#F0D6E2", "Magenta 200"),
    magenta300 : new ColorOption("magenta300", "#E9C2D4", "Magenta 300"),
    magenta400 : new ColorOption("magenta400", "#DEA5BF", "Magenta 400"),
    magenta500 : new ColorOption("magenta500", "#D68EAF", "Magenta 500"),
    magenta600 : new ColorOption("magenta600", "#CE779E", "Magenta 600"),
    magenta700 : new ColorOption("magenta700", "#C6618E", "Magenta 700"),
    magenta800 : new ColorOption("magenta800", "#BD4A7E", "Magenta 800"),
    magenta900 : new ColorOption("magenta900", "#B5346E", "Magenta 900"),
    magenta1000: new ColorOption("magenta1000", "#791442", "Magenta 1000")
} as const;

export const CoreStackGCP = {
    gcp100 : new ColorOption("gcp100", "#A1C2FA", "GCP 100"),
    gcp200 : new ColorOption("gcp200", "#8EB6F8", "GCP 200"),
    gcp300 : new ColorOption("gcp300", "#7BAAF7", "GCP 300"),
    gcp400 : new ColorOption("gcp400", "#689DF6", "GCP 400"),
    gcp500 : new ColorOption("gcp500", "#5591F5", "GCP 500"),
    gcp600 : new ColorOption("gcp600", "#4285F4", "GCP 600"),
    gcp700 : new ColorOption("gcp700", "#3B78DC", "GCP 700"),
    gcp800 : new ColorOption("gcp800", "#356AC3", "GCP 800"),
    gcp900 : new ColorOption("gcp900", "#2E5DAB", "GCP 900"),
    gcp1000: new ColorOption("gcp1000", "#285092", "GCP 1000")
} as const;

export const CoreStackAWS = {
    aws100 : new ColorOption("aws100", "#FFC266", "AWS 100"),
    aws200 : new ColorOption("aws200", "#FFB84D", "AWS 200"),
    aws300 : new ColorOption("aws300", "#FFAD33", "AWS 300"),
    aws400 : new ColorOption("aws400", "#FFA31A", "AWS 400"),
    aws500 : new ColorOption("aws500", "#FF9900", "AWS 500"),
    aws600 : new ColorOption("aws600", "#E68A00", "AWS 600"),
    aws700 : new ColorOption("aws700", "#CC7A00", "AWS 700"),
    aws800 : new ColorOption("aws800", "#B36B00", "AWS 800"),
    aws900 : new ColorOption("aws900", "#995C00", "AWS 900"),
    aws1000: new ColorOption("aws1000", "#804D00", "AWS 1000")
} as const;

export const CoreStackAzure = {
    azure100 : new ColorOption("azure100", "#66DAFF", "Azure 100"),
    azure200 : new ColorOption("azure200", "#4DD4FF", "Azure 200"),
    azure300 : new ColorOption("azure300", "#33CEFF", "Azure 300"),
    azure400 : new ColorOption("azure400", "#1AC8FF", "Azure 400"),
    azure500 : new ColorOption("azure500", "#00C2FF", "Azure 500"),
    azure600 : new ColorOption("azure600", "#00AFE6", "Azure 600"),
    azure700 : new ColorOption("azure700", "#009BCC", "Azure 700"),
    azure800 : new ColorOption("azure800", "#0088B3", "Azure 800"),
    azure900 : new ColorOption("azure900", "#007499", "Azure 900"),
    azure1000: new ColorOption("azure1000", "#006180", "Azure 1000")
} as const;

export const CoreStackOracle = {
    oracle100 : new ColorOption("oracle100", "#DD9085", "OCI 100"),
    oracle200 : new ColorOption("oracle200", "#D87E71", "OCI 200"),
    oracle300 : new ColorOption("oracle300", "#D26B5D", "OCI 300"),
    oracle400 : new ColorOption("oracle400", "#CD5948", "OCI 400"),
    oracle500 : new ColorOption("oracle500", "#C74634", "OCI 500"),
    oracle600 : new ColorOption("oracle600", "#B33F2F", "OCI 600"),
    oracle700 : new ColorOption("oracle700", "#9F382A", "OCI 700"),
    oracle800 : new ColorOption("oracle800", "#8B3124", "OCI 800"),
    oracle900 : new ColorOption("oracle900", "#772A1F", "OCI 900"),
    oracle1000: new ColorOption("oracle1000", "#64231A", "OCI 1000")
} as const;

export const SemanticColorPalette = {
    semanticBlue   : new ColorOption("semanticBlue", "#0076E1", "Semantic Blue"),
    semanticNeutral: new ColorOption("semanticNeutral", "#E1E4E9", "Semantic Neutral"),
    semanticGreen  : new ColorOption("semanticGreen", "#36B37E", "Semantic Green"),
    semanticYellow : new ColorOption("semanticYellow", "#D6A730", "Semantic Yellow"),
    semanticOrange : new ColorOption("semanticOrange", "#F55826", "Semantic Orange"),
    semanticMagenta: new ColorOption("semanticMagenta", "#AD1D5E", "Semantic Magenta")
} as const;

export const HeatmapRedColors = {
    Red100: new ColorOption("Red100", "#e8c5ca", "Red 100"),
    Red200: new ColorOption("Red200", "#daa5ac", "Red 200"),
    Red300: new ColorOption("Red300", "#cc8590", "Red 300"),
    Red400: new ColorOption("Red400", "#bf6674", "Red 400"),
    Red500: new ColorOption("Red500", "#b14758", "Red 500")
} as const;

export type PaletteId = keyof typeof colorPalettes;
export type ColorIds<K extends PaletteId> = keyof typeof colorOptionPalettes[K];
export type DefaultColorIds = keyof typeof DefaultColorPalette;

const colorOptionPalettes: Lookup<Lookup<ColorOption>> = {
    null             : DefaultColorPalette,
    "Warm"           : {
        warm0: new ColorOption("warm0", "hsl(303,70%,65%)", "Violet"),
        warm1: new ColorOption("warm1", "hsl(317,70%,65%)", "Orchid"),
        warm2: new ColorOption("warm2", "hsl(331,70%,65%)", "Warm Orchid"),
        warm3: new ColorOption("warm3", "hsl(345,70%,65%)", "Violet Red"),
        warm4: new ColorOption("warm4", "hsl(360,70%,65%)", "Red"),
        warm5: new ColorOption("warm5", "hsl(12,70%,65%)", "Light Coral"),
        warm6: new ColorOption("warm6", "hsl(24,70%,65%)", "Dark Salmon"),
        warm7: new ColorOption("warm7", "hsl(36,70%,65%)", "Sandy Brown"),
        warm8: new ColorOption("warm8", "hsl(48,70%,65%)", "Burly Wood"),
        warm9: new ColorOption("warm9", "hsl(60,70%,65%)", "Khaki")
    },
    "Cold"           : {
        cold0: new ColorOption("cold0", "hsl(110,70%,65%)", "Yellow Green"),
        cold1: new ColorOption("cold1", "hsl(135,70%,65%)", "Lush Green"),
        cold2: new ColorOption("cold2", "hsl(160,70%,65%)", "Aquamarine"),
        cold3: new ColorOption("cold3", "hsl(180,70%,65%)", "Cyan"),
        cold4: new ColorOption("cold4", "hsl(195,70%,65%)", "Sky Blue"),
        cold5: new ColorOption("cold5", "hsl(210,70%,65%)", "Corn Flower Blue"),
        cold6: new ColorOption("cold6", "hsl(225,70%,65%)", "Blue"),
        cold7: new ColorOption("cold7", "hsl(240,70%,65%)", "Slate Blue"),
        cold8: new ColorOption("cold8", "hsl(255,70%,65%)", "Dark Periwinkle"),
        cold9: new ColorOption("cold9", "hsl(270,70%,65%)", "Purple")
    },
    "Black-White"    : mapPalette(getGradientPalette("Black-White", "#000000", "#FFFFFF", 10, 0, 0)),
    "Green-Red"      : mapPalette(getGradientPalette("Green-Red", "#7DE87D", "#E87D8F")),
    "Red-Blue"       : mapPalette(getGradientPalette("Red-Blue", "#E87D8F", "#7DAAE8")),
    "Blue-Green"     : mapPalette(getGradientPalette("Blue-Green", "#7DAAE8", "#7DE87D")),
    "Magenta-Cyan"   : mapPalette(getGradientPalette("Magenta-Cyan", "#E87DBB", "#7DE8C4")),
    "Cyan-Yellow"    : mapPalette(getGradientPalette("Cyan-Yellow", "#7DE8C4", "#E8D67D")),
    "Yellow-Magenta" : mapPalette(getGradientPalette("Yellow-Magenta", "#E8D67D", "#E87DBB")),
    "Red"            : {
        red0: new ColorOption("red0", "#E57373", "Dark Salmon"),
        red1: new ColorOption("red1", "#EF5552", "Tomato"),
        red2: new ColorOption("red2", "#DB3333", "Fire Brick"),
        red3: new ColorOption("red3", "#C62828", "Crimson"),
        red4: new ColorOption("red4", "#B71C1C", "Dark Red")
    },
    "Orange"         : {
        orange0: new ColorOption("orange0", "#FFAF3D", "Sand Orange"),
        orange1: new ColorOption("orange1", "#FF9800", "Light Orange"),
        orange2: new ColorOption("orange2", "#f88400", "Orange"),
        orange3: new ColorOption("orange3", "#EF6C00", "Dark Orange"),
        orange4: new ColorOption("orange4", "#E65100", "Red Orange")
    },
    "Yellow"         : {
        yellow0: new ColorOption("yellow0", "#FFFF8D", "Light Yellow"),
        yellow1: new ColorOption("yellow1", "#FFFF46", "Yellow"),
        yellow2: new ColorOption("yellow2", "#FFEA00", "Yellow Gold"),
        yellow3: new ColorOption("yellow3", "#FFD600", "Gold"),
        yellow4: new ColorOption("yellow4", "#FBC12E", "Goldenrod")
    },
    "Green"          : {
        green0: new ColorOption("green0", "#81C784", "Light Sea Green"),
        green1: new ColorOption("green1", "#58B560", "Sea Green"),
        green2: new ColorOption("green2", "#43A047", "Dark Sea Green"),
        green3: new ColorOption("green3", "#2E7D32", "Forest Green"),
        green4: new ColorOption("green4", "#1B5E20", "Dark Green")
    },
    "Blue"           : {
        blue0: new ColorOption("blue0", "#52ACF5", "Sky Blue"),
        blue1: new ColorOption("blue1", "#2196F3", "Light Blue"),
        blue2: new ColorOption("blue2", "#1C7FDB", "Dodger Blue"),
        blue3: new ColorOption("blue3", "#1565C0", "Royal Blue"),
        blue4: new ColorOption("blue4", "#0D47A1", "Dark Slate Blue")
    },
    "Purple"         : {
        purple0: new ColorOption("purple0", "#AB47BC", "Orchid"),
        purple1: new ColorOption("purple1", "#9C27B0", "Dark Orchid"),
        purple2: new ColorOption("purple2", "#8522a6", "Dark Magenta"),
        purple3: new ColorOption("purple3", "#6A1B9A", "Purple"),
        purple4: new ColorOption("purple4", "#4A148C", "Indigo Purple")
    },
    "Gray"           : {
        gray0: new ColorOption("gray0", "#859AA5", "Light Gray"),
        gray1: new ColorOption("gray1", "#607D8B", "Light Slate Gray"),
        gray2: new ColorOption("gray2", "#4D6470", "Dim Gray"),
        gray3: new ColorOption("gray3", "#37474F", "Slate Gray"),
        gray4: new ColorOption("gray4", "#263238", "Dark Slate Gray"),
        gray5: new ColorOption("gray5", "#f2f2f2", "very light gray")
    },
    "Cloud Colors"   : {
        AWS  : new ColorOption("AWS", "#FF9900", "AWS"),
        Azure: new ColorOption("Azure", "#00C2FF", "Azure"),
        GCP  : new ColorOption("GCP", "#4285F4", "GCP"),
        OCI  : new ColorOption("OCI", "#C74634", "OCI")
    },
    "Map Colors"     : {
        mapred   : new ColorOption("mapred", "#FF5F5B", "Map Red"),
        mapgreen : new ColorOption("mapgreen", "#5BE43D", "Map Green"),
        maporange: new ColorOption("maporange", "#FF891C", "Map Orange"),
        mapblue  : new ColorOption("mapblue", "#3776F4", "Map Blue"),
        mapyellow: new ColorOption("mapyellow", "#E7DA39", "Map Yellow")
    },
    "Map Path Colors": {
        mapred   : new ColorOption("mapred", "#FF5F5B", "Map Red"),
        mapgreen : new ColorOption("mapgreen", "#5BE43D", "Map Green"),
        maporange: new ColorOption("maporange", "#FF891C", "Map Orange"),
        mapblue  : new ColorOption("mapblue", "#3776F4", "Map Blue"),
        mappurple: new ColorOption("mappurple", "#D480F5", "Map Purple")
    },
    "Severity Colors": {
        critical     : new ColorOption("critical", SemanticColorPalette.semanticMagenta.hex, "Critical"),
        high         : new ColorOption("high", SemanticColorPalette.semanticOrange.hex, "High"),
        medium       : new ColorOption("medium", SemanticColorPalette.semanticYellow.hex, "Medium"),
        low          : new ColorOption("low", SemanticColorPalette.semanticGreen.hex, "Low"),
        info         : new ColorOption("info", SemanticColorPalette.semanticBlue.hex, "Info"),
        informational: new ColorOption("informational", SemanticColorPalette.semanticBlue.hex, "Informational"),
        unknown      : new ColorOption("unknown", CoreStackTeal.teal500.hex, "Unknown"),
        none         : new ColorOption("none", CoreStackTeal.teal700.hex, "None"),
        invalid      : new ColorOption("invalid", SemanticColorPalette.semanticNeutral.hex, "Invalid")
    }
};

class ColorPalette
{
    constructor(public readonly label: string,
                public readonly availableToUser: boolean,
                public readonly colorOptions: Lookup<ColorOption>)
    {
    }
}

const colorPalettes = {
    ""                             : new ColorPalette("Default", true, DefaultColorPalette),
    "Warm"                         : new ColorPalette("Warm", true, colorOptionPalettes["Warm"]),
    "Cold"                         : new ColorPalette("Cold", true, colorOptionPalettes["Cold"]),
    "Black-White"                  : new ColorPalette("Black-White", true, colorOptionPalettes["Black-White"]),
    "Green-Red"                    : new ColorPalette("Green-Red", true, colorOptionPalettes["Green-Red"]),
    "Red-Blue"                     : new ColorPalette("Red-Blue", true, colorOptionPalettes["Red-Blue"]),
    "Blue-Green"                   : new ColorPalette("Blue-Green", true, colorOptionPalettes["Blue-Green"]),
    "Magenta-Cyan"                 : new ColorPalette("Magenta-Cyan", true, colorOptionPalettes["Magenta-Cyan"]),
    "Cyan-Yellow"                  : new ColorPalette("Cyan-Yellow", true, colorOptionPalettes["Cyan-Yellow"]),
    "Yellow-Magenta"               : new ColorPalette("Yellow-Magenta", true, colorOptionPalettes["Yellow-Magenta"]),
    "Red"                          : new ColorPalette("Red", true, colorOptionPalettes["Red"]),
    "Orange"                       : new ColorPalette("Orange", true, colorOptionPalettes["Orange"]),
    "Yellow"                       : new ColorPalette("Yellow", true, colorOptionPalettes["Yellow"]),
    "Green"                        : new ColorPalette("Green", true, colorOptionPalettes["Green"]),
    "Blue"                         : new ColorPalette("Blue", true, colorOptionPalettes["Blue"]),
    "Purple"                       : new ColorPalette("Purple", true, colorOptionPalettes["Purple"]),
    "Gray"                         : new ColorPalette("Gray", true, colorOptionPalettes["Gray"]),
    "Cloud Colors"                 : new ColorPalette("Cloud Colors", false, colorOptionPalettes["Cloud Colors"]),
    "Map Colors"                   : new ColorPalette("Map Colors", false, colorOptionPalettes["Map Colors"]),
    "Map Path Colors"              : new ColorPalette("Map Path Colors", false, colorOptionPalettes["Map Path Colors"]),
    "Severity Colors"              : new ColorPalette("Severity Colors", false, colorOptionPalettes["Severity Colors"]),
    "CoreStack"                    : new ColorPalette("CoreStack Theme", false, CoreStackChartColorPalette),
    "CoreStack Blue Gradient"      : new ColorPalette("CoreStack Blue Gradient", false, CoreStackBlueGradient),
    "CoreStack Red Gradient"       : new ColorPalette("CoreStack Red Gradient", false, CoreStackRedGradient),
    "CoreStack Orange Gradient"    : new ColorPalette("CoreStack Orange Gradient", false, CoreStackOrangeGradient),
    "CoreStack Green Gradient"     : new ColorPalette("CoreStack Green Gradient", false, CoreStackGreenGradient),
    "CoreStack Brown Gradient"     : new ColorPalette("CoreStack Brown Gradient", false, CoreStackBrownGradient),
    "CoreStack Pink Gradient"      : new ColorPalette("CoreStack Pink Gradient", false, CoreStackPinkGradient),
    "CoreStack Lavender Gradient"  : new ColorPalette("CoreStack Lavender Gradient", false, CoreStackLavenderGradient),
    "CoreStack Yellow Gradient"    : new ColorPalette("CoreStack Yellow Gradient", false, CoreStackYellowGradient),
    "CoreStack Purple Gradient"    : new ColorPalette("CoreStack Purple Gradient", false, CoreStackPurpleGradient),
    "CoreStack Aquamarine Gradient": new ColorPalette("CoreStack Aquamarine Gradient", false, CoreStackAquamarineGradient),
    "CoreStack Black"              : new ColorPalette("CoreStack Black", false, CoreStackBlack),
    "CoreStack Blue"               : new ColorPalette("CoreStack Blue", false, CoreStackBlue),
    "CoreStack Yellow"             : new ColorPalette("CoreStack Yellow", false, CoreStackYellow),
    "CoreStack Orange"             : new ColorPalette("CoreStack Orange", false, CoreStackOrange),
    "CoreStack Green"              : new ColorPalette("CoreStack Green", false, CoreStackGreen),
    "CoreStack Magenta"            : new ColorPalette("CoreStack Magenta", false, CoreStackMagenta)
};

export enum CoreStackChartColors
{
    Blue       = "blue",
    Red        = "red",
    Orange     = "orange",
    Green      = "green",
    Brown      = "brown",
    Pink       = "pink",
    Lavender   = "lavender",
    Yellow     = "yellow",
    Purple     = "purple",
    Aquamarine = "aquamarine"
}

export enum TierColorTheme
{
    Teal    = "teal",
    Purple  = "purple",
    Green   = "green",
    Orange  = "orange",
    Blue    = "blue",
    Magenta = "magenta",
}
export class ChartColorUtilities
{
    public static readonly defaultGradient           = "Green-Red";
    public static readonly defaultPalette: PaletteId = "";

    public static readonly palettes: Lookup<ColorPalette> = colorPalettes;
    private static colorToPalette: Lookup<PaletteId>;

    public static gradientPresets: Lookup<ColorScheme> = {
        "Black-White"   : new ColorScheme([
                                              "#000000",
                                              "#FFFFFF"
                                          ]),
        "Green-Red"     : new ColorScheme([
                                              ChartColorUtilities.getDefaultColorById("green").hex,
                                              ChartColorUtilities.getDefaultColorById("red").hex
                                          ]),
        "Red-Blue"      : new ColorScheme([
                                              ChartColorUtilities.getDefaultColorById("red").hex,
                                              ChartColorUtilities.getDefaultColorById("blue").hex
                                          ]),
        "Blue-Green"    : new ColorScheme([
                                              ChartColorUtilities.getDefaultColorById("blue").hex,
                                              ChartColorUtilities.getDefaultColorById("green").hex
                                          ]),
        "Magenta-Cyan"  : new ColorScheme([
                                              ChartColorUtilities.getDefaultColorById("pink").hex,
                                              ChartColorUtilities.getDefaultColorById("teal").hex
                                          ]),
        "Cyan-Yellow"   : new ColorScheme([
                                              ChartColorUtilities.getDefaultColorById("teal").hex,
                                              ChartColorUtilities.getDefaultColorById("yellow").hex
                                          ]),
        "Yellow-Magenta": new ColorScheme([
                                              ChartColorUtilities.getDefaultColorById("yellow").hex,
                                              ChartColorUtilities.getDefaultColorById("pink").hex
                                          ]),
        "Reds"          : new ColorScheme([
                                              "#E57373",
                                              "#AF1010"
                                          ]),
        "Oranges"       : new ColorScheme([
                                              "#FFBF66",
                                              "#E65100"
                                          ]),
        "Yellows"       : new ColorScheme([
                                              "#FFFF8D",
                                              "#FBC12E"
                                          ]),
        "Greens"        : new ColorScheme([
                                              "#81C784",
                                              "#1B5E20"
                                          ]),
        "Blues"         : new ColorScheme([
                                              "#73BCF7",
                                              "#0D47A1"
                                          ]),
        "Purples"       : new ColorScheme([
                                              "#C37CD0",
                                              "#4A148C"
                                          ]),
        "Pinks"         : new ColorScheme([
                                              "#F48FB1",
                                              "#AD1457"
                                          ]),
        "Grays"         : new ColorScheme([
                                              "#859AA5",
                                              "#263238"
                                          ])
    };

    public static readonly CHIP_BACKGROUND_LUMINANCE: number = 0.87;

    public static getPalette(paletteId: PaletteId): Lookup<ColorOption>
    {
        const palette = ChartColorUtilities.palettes[paletteId] || ChartColorUtilities.palettes[""];
        return palette.colorOptions;
    }

    public static getPaletteOptions(paletteId?: PaletteId): ColorOption[]
    {
        let colorOptions = ChartColorUtilities.getPalette(paletteId);
        if (!colorOptions) colorOptions = ChartColorUtilities.getPalette(ChartColorUtilities.defaultPalette);
        return Object.keys(colorOptions)
                     .map((color) => colorOptions[color]);
    }

    public static getGradientPreset(preset?: string): ColorScheme
    {
        return ChartColorUtilities.gradientPresets[preset] || ChartColorUtilities.gradientPresets[ChartColorUtilities.defaultGradient];
    }

    public static paletteOptions(): ControlOption<PaletteId>[]
    {
        const options = [];
        for (const paletteId in ChartColorUtilities.palettes)
        {
            const palette = ChartColorUtilities.palettes[paletteId];
            if (palette?.availableToUser) options.push(new ControlOption(<PaletteId>paletteId, palette.label));
        }

        return options;
    }

    public static gradientPresetNames(): string[]
    {
        let names = [];
        for (const name in ChartColorUtilities.gradientPresets)
        {
            names.push(name);
        }

        return names;
    }

    public static getDefaultColorById(id: DefaultColorIds): ColorOption
    {
        return DefaultColorPalette[id];
    }

    public static getColorById<K extends PaletteId>(paletteId: K,
                                                    id: ColorIds<K>): ColorOption
    {
        let palette = ChartColorUtilities.getPalette(paletteId);
        return palette ? palette[id] : undefined;
    }

    private static readonly SEVERITY_PALETTE: Lookup<ColorOption> = ChartColorUtilities.getPalette("Severity Colors");

    public static getSeverityColor(severity: string): string
    {
        return ChartColorUtilities.SEVERITY_PALETTE[severity.toLowerCase()]?.hex
               ?? ChartColorUtilities.getColorById("Severity Colors", "unknown").color;
    }

    public static getTopologyTierColor(index: number): TierColorTheme
    {
        const values = Object.values(TierColorTheme);
        return values[index % values.length];
    }

    /**
     *
     * @param colorClass
     * @param darknessLevel from 1-10 where 1 is lightest color in gradient and 10 is darkest
     */
    public static getChartGradientColor(colorClass: CoreStackChartColors,
                                        darknessLevel: number): string
    {
        colorClass ||= CoreStackChartColors.Blue;
        darknessLevel = UtilsService.clamp(1, 10, Math.round(darknessLevel));

        return ChartColorUtilities.getColorById(<PaletteId>`CoreStack ${UtilsService.capitalizeFirstLetterAllWords(colorClass)} Gradient`,
                                                `${colorClass}${darknessLevel}0`).hex;
    }

    public static getColorOptions(palette?: PaletteId): ControlOption<ColorOption>[]
    {
        let colors = ChartColorUtilities.getPaletteOptions(palette);
        return colors.map((color) => new ControlOption(color, color.name));
    }

    public static nextBestColor(colors: string[],
                                palette?: PaletteId,
                                skipAdd?: boolean): string
    {
        let bestColor;
        let activePalette = ChartColorUtilities.getPaletteOptions(palette);
        if (colors.length === 0)
        {
            bestColor = activePalette[0].hex;
        }
        else
        {
            // Map all used colors in palette to number of uses
            let usage = new Map<string, number>();
            for (let usedColor of colors)
            {
                if (usedColor)
                {
                    if (!activePalette.find((color) => color.hex === usedColor)) continue;

                    if (usage.has(usedColor))
                    {
                        usage.set(usedColor, usage.get(usedColor) + 1);
                    }
                    else
                    {
                        usage.set(usedColor, 1);
                    }
                }
            }

            let unusedColor;
            for (let i = 0; i < activePalette.length; i++)
            {
                let color = ChartColorUtilities.getColor(i, palette, true);
                if (!usage.has(color))
                {
                    unusedColor = color;
                    break;
                }
            }

            if (unusedColor)
            {
                bestColor = unusedColor;
            }
            else
            {
                let colorsByUsageAscending = Array.from(usage.entries())
                                                  .sort((a,
                                                         b) => a[1] - b[1]);

                bestColor = colorsByUsageAscending[0][0];
            }
        }

        if (!skipAdd) colors.push(bestColor);
        return bestColor;
    }

    /**
     * Get background colors for elements of a chart type that can display multiple colors (e.g. bar chart).
     * @param paletteId - The palette to pull the colors from
     * @param {number} total - The number of colors to retrieve; should match the number of a data points in the chart.
     * @param {number} [monoColorIndex] - The index of the color to select from the default chart colors; to be used if chart should be monochrome.
     */
    public static getColors(paletteId: PaletteId,
                            total: number,
                            monoColorIndex?: number): string[]
    {
        let palette = ChartColorUtilities.getPaletteOptions(paletteId);
        total ||= palette.length;

        let colors: string[] = [];
        for (let i = 0; i < total; i++)
        {
            let colorIndex = (monoColorIndex || i) % palette.length;
            let color      = palette[colorIndex];
            colors.push(color.hex);
        }
        return colors;
    };

    /**
     * Get a background color for a chart type that can only display a single color (e.g. line chart).
     * @param {number} [itemIndex=0] - The index of the color to select from the default chart colors.
     * @param palette - optionally select palette other than defaultPalette
     * @param shuffle
     */
    public static getColor(itemIndex: number = 0,
                           palette: PaletteId,
                           shuffle: boolean): string
    {
        let paletteOptions = ChartColorUtilities.getPaletteOptions(palette);

        if (shuffle) itemIndex *= 3;
        itemIndex ||= 0;

        return paletteOptions[itemIndex % paletteOptions.length].hex;
    }

    public static getComplementaryColor(from: string): string
    {
        let hsv = ChartColorUtilities.safeChroma(from)
                                     .hsv();

        return chroma.hsv(hsv[0] + 180, hsv[1], hsv[2])
                     .hex();
    }

    public static getTransparentColorOnBackground(color: string,
                                                  opacity: number,
                                                  backgroundColor: string = "white"): string
    {
        opacity        = UtilsService.clamp(0, 1, opacity);
        const chroma   = ChartColorUtilities.safeChroma(color);
        const bgChroma = ChartColorUtilities.safeChroma(backgroundColor);
        const colorRGB = chroma.rgb();
        const bgRGB    = bgChroma.rgb();

        return chroma.set("rgb.r", opacity * colorRGB[0] + (1 - opacity) * bgRGB[0])
                     .set("rgb.g", opacity * colorRGB[1] + (1 - opacity) * bgRGB[1])
                     .set("rgb.b", opacity * colorRGB[2] + (1 - opacity) * bgRGB[2])
                     .hex("rgb");
    }

    public static getPaletteFromColor(color: string): PaletteId
    {
        ChartColorUtilities.ensureColorToPalette();

        if (ChartColorUtilities.colorToPalette.hasOwnProperty(color))
        {
            return ChartColorUtilities.colorToPalette[color] || ChartColorUtilities.defaultPalette;
        }

        return null;
    }

    private static ensureColorToPalette()
    {
        if (!ChartColorUtilities.colorToPalette)
        {
            ChartColorUtilities.colorToPalette = {};
            for (let paletteId in ChartColorUtilities.palettes)
            {
                let palette = ChartColorUtilities.palettes[paletteId];
                for (let colorId in palette.colorOptions)
                {
                    ChartColorUtilities.colorToPalette[palette.colorOptions[colorId].hex] = <PaletteId>paletteId;
                }
            }
        }
    }

    public static safeChroma(from: string | number): chroma.Color
    {
        return safeChroma(from);
    }

    /**
     * For the given color (input), pick one if the two given colors (colorA, colorB) that has more contrast.
     * @param {number | string} input - The color to compute contrast against.
     * @param {number | string} [colorA="white"] - The first color to compare against the input color.
     * @param {number | string} [colorB="black"] - The second color to compare against the input color.
     * @param {number} [skew=0.5] - Ranges from 0-1, used to skew in preference of colorA vs colorB, even if one has more contrast.
     */
    public static bestContrastBetween(input: string | number,
                                      colorA: string | number = "white",
                                      colorB: string | number = "black",
                                      skew: number            = 0.5): chroma.Color
    {
        let color = this.safeChroma(input);
        let a     = this.safeChroma(colorA);
        let b     = this.safeChroma(colorB);

        skew *= 2;
        let skewA = Math.min(1, skew);
        let skewB = Math.min(1, 2 - skew);

        if (skewA <= 0) return a;
        if (skewB <= 0) return b;

        let aContrast = chroma.contrast(a, color) * skewB;
        let bContrast = chroma.contrast(b, color) * skewA;
        return aContrast >= bContrast ? a : b;
    }

    public static lighterBackgroundColor(color: string): string
    {
        const base     = ChartColorUtilities.safeChroma(color);
        let background = base.luminance(ChartColorUtilities.CHIP_BACKGROUND_LUMINANCE);

        const contrastThreshold = 1.445; // experimentally found
        while (chroma.contrast(base, background) < contrastThreshold)
        {
            background = background.brighten(0.05);
            if (background.hex() === "#ffffff") break;
        }

        return background.hex();
    }

    public static progressColorFn      = (value: number) => ChartColorUtilities.progressColorMapper.getColor(value);
    private static progressColorMapper = new StepwiseColorMapper([
                                                                     new ColorGradientStop(0, ChartColorUtilities.getColorById("Severity Colors", "critical").hex),
                                                                     new ColorGradientStop(1, ChartColorUtilities.getColorById("Severity Colors", "high").hex),
                                                                     new ColorGradientStop(100, ChartColorUtilities.getColorById("Severity Colors", "low").hex)
                                                                 ]);
}

export type Fill = string | CanvasPattern | CanvasGradient;
